import { useState } from 'react';
import dynamic from 'next/dynamic';
import type { PDPLayoutSlot as PDPLayoutSchema } from '@amplience/content-types/typings/s-pdp-layout';
import { CMS_SLOTS_DELIVERY_KEYS, CmsSlot } from '@storefront/lib-global';
import type { CarouselProduct, GiftCardProduct } from '@storefront/lib-global/codegen/types';
import type { HandlebarTemplateProps } from '@storefront/lib-global/globalComponents/handlebarTemplate';
import { OccasionAndDenominationContextProvider } from '../../../context/occasionAndDenomination';
import type { SupportedGiftCardTypes } from '../../../types';
import ProductName from '../ProductName/ProductName';
import { BelowTheFoldSection } from '../belowTheFoldSection/BelowTheFoldSection';
import { GiftCardInfo } from '../giftCardInfo/GiftCardInfo';
import ProductBreadcrumb from '../productBreadcrumb/ProductBreadcrumb';
import ProductMedia from '../productMedia/ProductMedia';
import { MobileProductNameContainer, Product, ProductPageContainer, ProductPageContent } from './GiftCardDetail.styles';

export type GiftCardDetailProps = {
  pdpLayout: PDPLayoutSchema;
  giftCard: GiftCardProduct;
  carouselData: { deliveryId: string; carouselProducts: CarouselProduct[] }[];
  handlebarComponents: HandlebarTemplateProps[] | null;
  supportedTypes: SupportedGiftCardTypes;
  shouldShowDesktopH1?: boolean;
  shouldShowMobileH1?: boolean;
};

const DynamicProductZoom = dynamic(
  async () => {
    const { ProductZoom } = await import('../productZoom/ProductZoom');
    return ProductZoom;
  },
  { ssr: false },
);

export function GiftCardDetail({
  giftCard,
  pdpLayout,
  carouselData,
  handlebarComponents,
  supportedTypes,
  shouldShowDesktopH1,
  shouldShowMobileH1,
}: GiftCardDetailProps) {
  const { commercetoolsType, key, giftCardVariants, name, references, categoriesPath, badgesV2, subTypes } = {
    ...giftCard,
  };
  const { slotContent = null } = { ...pdpLayout };

  const [isZoomActive, setIsZoomActive] = useState(false);
  const [activeMediaSlide, setActiveMediaSlide] = useState(0);

  const productMediaUrls = [giftCardVariants[0]?.imageUrls[0]];
  const isPDPGiftCardType = commercetoolsType === 'giftCard' || commercetoolsType === 'egiftCard';

  return (
    <>
      {categoriesPath && categoriesPath.length > 0 && <ProductBreadcrumb categoriesPath={categoriesPath} />}
      <ProductPageContainer>
        <OccasionAndDenominationContextProvider giftCardVariants={giftCardVariants ?? []}>
          {isZoomActive && (
            <DynamicProductZoom
              productMediaUrls={productMediaUrls}
              isZoomActive={isZoomActive}
              setIsZoomActive={setIsZoomActive}
              activeMediaSlide={activeMediaSlide}
              setActiveMediaSlide={setActiveMediaSlide}
              isPDPGiftCardType={isPDPGiftCardType}
              productName={name ?? ''}
              productKey={key}
            />
          )}

          <ProductPageContent>
            {shouldShowMobileH1 && (
              <MobileProductNameContainer data-testid="mobileTitleContainer">
                <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_ABOVE_NAME_BANNER} />
                <ProductName name={name ?? ''} />
              </MobileProductNameContainer>
            )}

            <Product $isZoomOverlayActive={isZoomActive} data-testid="productContainerDesktop">
              <ProductMedia
                isPDPGiftCardType={isPDPGiftCardType}
                productMediaUrls={productMediaUrls}
                setIsZoomActive={setIsZoomActive}
                activeMediaSlide={activeMediaSlide}
                setActiveMediaSlide={setActiveMediaSlide}
                badgesV2={badgesV2}
                subTypes={subTypes ?? undefined}
                productName={name as string}
              />
              <GiftCardInfo
                shouldShowDesktopH1={shouldShowDesktopH1}
                slotContent={slotContent}
                name={name as string}
                giftCardType={giftCard.commercetoolsType}
                references={references || null}
                handlebarComponents={handlebarComponents}
                supportedTypes={supportedTypes}
              />
            </Product>
          </ProductPageContent>
        </OccasionAndDenominationContextProvider>
      </ProductPageContainer>
      <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_BELOW_PRODUCT_RECS_BANNER} />
      <BelowTheFoldSection
        belowTheFoldContent={slotContent?.belowTheFoldContent}
        carouselData={carouselData}
        singleProductKey={key}
        handlebarComponents={handlebarComponents}
        isZoomActive={isZoomActive}
      />
      <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.PDP_ABOVE_FOOTER_BANNER} />
    </>
  );
}

export default GiftCardDetail;
